import { useEffect, useRef, useState } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigation, useSearchParams } from "react-router";
import { MOBILE_PRIMARY, MOBILE_SECONDARY, NAV_ITEMS, sectionOf } from "../lib/shell-views";
import { Icon, isIconName } from "../ui/Icon";
import { useDialog } from "../ui/useDialog";
import { Portal } from "../ui/Portal";
import { useRuleEditor } from "../features/rule-editor/useRuleEditor";
import { ProfileImage } from "./ProfileImage";
import { isSetupDismissed } from "./setup-dismissal";
import { notify } from "./toast";
import { useBusy } from "./useRun";
import { usePanel } from "./usePanel";

/**
 * The frame around every section.
 *
 * On a desktop, a sidebar with the sections, the account's live state and the main action.
 * On a phone, a slim top bar and a bottom bar with the four sections used daily, the "+"
 * that creates an automation in the middle, and "Más" for the rest.
 */
export function AppLayout() {
  const { config, connection, stats } = usePanel();
  const busy = useBusy();
  const location = useLocation();
  const editor = useRuleEditor();
  const [moreOpen, setMoreOpen] = useState(false);
  const main = useRef<HTMLElement>(null);
  // A section opened for the first time downloads its code: the bar says something is coming.
  const loading = useNavigation().state === "loading";
  useConnectionOutcome();

  // A new section starts at the top, as a new page would. Only the section counts: opening a
  // conversation or a rule changes the address too, and must not throw the list back up.
  const section = sectionOf(location.pathname);
  useEffect(() => {
    window.scrollTo({ top: 0 });
    setMoreOpen(false);
  }, [section]);

  // Setup blocks the panel while anything is missing, but it can be postponed to see the
  // application in simulation mode.
  // It remembers where one was going, so postponing it lands there and not on the summary.
  if (config.needsSetup && !isSetupDismissed()) {
    return <Navigate to="/conexion" replace state={{ from: location.pathname + location.search }} />;
  }

  // What needs looking at, counted on the section it belongs to.
  const counts: Record<string, { value: number; tone: "danger" | "accent" } | undefined> = {
    "/actividad": stats.failed > 0 ? { value: stats.failed, tone: "danger" } : undefined,
    "/conversaciones": stats.awaitingReply > 0 ? { value: stats.awaitingReply, tone: "accent" } : undefined,
  };
  const moreActive = MOBILE_SECONDARY.some((path) => sectionOf(path) === section);
  const moreCount = MOBILE_SECONDARY.reduce((sum, path) => sum + (counts[path]?.value ?? 0), 0);

  return (
    <div className="cs-shell">
      {loading ? <div className="cs-route-progress" role="progressbar" aria-label="Cargando la sección" /> : null}
      <aside className="cs-sidebar" aria-label="Navegación principal">
        <Link to="/" className="cs-brand">
          <img className="cs-brand-mark" src="/InstaGain.png" alt="" />
          <span className="cs-brand-name">InstaGain</span>
        </Link>

        <button type="button" className="cs-sidebar-cta" disabled={busy} onClick={() => editor.create()}>
          <span className="cs-sidebar-cta-icon">
            <Icon name="plus" size={18} />
          </span>
          Nueva automatización
        </button>

        <nav className="cs-nav" aria-label="Secciones">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.path} to={item.path} end={item.path === "/"} className="cs-nav-item">
              {isIconName(item.icon) ? <Icon name={item.icon} size={19} /> : null}
              <span className="cs-nav-label">{item.label}</span>
              {counts[item.path] ? (
                <span className="cs-nav-count" data-tone={counts[item.path]!.tone}>
                  {counts[item.path]!.value}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>

        <div className="cs-sidebar-foot">
          <div className="cs-live" data-connected={connection.connected}>
            <span className="cs-live-dot" />
            <span>{connection.connected ? "Escuchando comentarios" : "Modo simulación"}</span>
          </div>
          <Link to="/ajustes" className="cs-profile">
            <ProfileImage alt={connection.profileAlt} />
            <span className="cs-profile-text">
              <span className="cs-profile-name">{connection.profileHandle}</span>
              <span className="cs-profile-label">{connection.profileLabel}</span>
            </span>
            <Icon name="settings" size={16} />
          </Link>
        </div>
      </aside>

      <header className="cs-topbar">
        <Link to="/" className="cs-topbar-brand" aria-label="Inicio">
          <img src="/InstaGain.png" alt="" />
        </Link>
        <span className="cs-topbar-title">InstaGain</span>
        <span className="cs-topbar-live" data-connected={connection.connected} title={connection.connectionStatus}>
          <span className="cs-live-dot" />
        </span>
        <Link to="/ajustes" className="cs-topbar-avatar" aria-label="Ajustes y cuenta">
          <ProfileImage alt={connection.profileAlt} />
        </Link>
      </header>

      <main ref={main} className="cs-main" aria-busy={busy}>
        <Outlet />
      </main>

      <nav className="cs-tabbar" aria-label="Secciones">
        {MOBILE_PRIMARY.slice(0, 2).map((path) => (
          <TabLink key={path} path={path} />
        ))}
        <button
          type="button"
          className="cs-tabbar-create"
          aria-label="Nueva automatización"
          disabled={busy}
          onClick={() => editor.create()}
        >
          <Icon name="plus" size={24} />
        </button>
        {MOBILE_PRIMARY.slice(2).map((path) => (
          <TabLink key={path} path={path} />
        ))}
        <button
          type="button"
          className="cs-tab"
          data-active={moreActive}
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen(true)}
        >
          <span className="cs-tab-icon">
            <Icon name="ellipsis" size={22} />
            {moreCount > 0 ? <span className="cs-tab-dot" /> : null}
          </span>
          <span className="cs-tab-label">Más</span>
        </button>
      </nav>

      {moreOpen ? <MoreSheet counts={counts} onClose={() => setMoreOpen(false)} /> : null}
    </div>
  );
}

function TabLink({ path }: { path: string }) {
  const item = NAV_ITEMS.find((candidate) => candidate.path === path)!;
  return (
    <NavLink to={item.path} end={item.path === "/"} className="cs-tab">
      <span className="cs-tab-icon">{isIconName(item.icon) ? <Icon name={item.icon} size={22} /> : null}</span>
      <span className="cs-tab-label">{item.shortLabel}</span>
    </NavLink>
  );
}

/** The sections that do not fit in the bottom bar, as a sheet rising from it. */
function MoreSheet({
  counts,
  onClose,
}: {
  counts: Record<string, { value: number; tone: string } | undefined>;
  onClose: () => void;
}) {
  const ref = useDialog<HTMLDivElement>(onClose);
  const { connection } = usePanel();
  return (
    <Portal>
      <div className="cs-sheet-scrim" onClick={onClose} role="presentation">
        <div
          ref={ref}
          className="cs-sheet"
          role="dialog"
          aria-modal="true"
          aria-label="Más secciones"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="cs-sheet-grip" aria-hidden="true" />
          <div className="cs-sheet-profile">
            <ProfileImage alt={connection.profileAlt} />
            <div>
              <strong>{connection.profileHandle}</strong>
              <span>{connection.connectionSub}</span>
            </div>
          </div>
          <div className="cs-sheet-grid">
            {MOBILE_SECONDARY.map((path) => {
              const item = NAV_ITEMS.find((candidate) => candidate.path === path)!;
              const count = counts[path];
              return (
                <NavLink key={path} to={path} className="cs-sheet-item" onClick={onClose}>
                  <span className="cs-sheet-icon">
                    {isIconName(item.icon) ? <Icon name={item.icon} size={22} /> : null}
                  </span>
                  <span>{item.label}</span>
                  {count ? (
                    <span className="cs-nav-count" data-tone={count.tone}>
                      {count.value}
                    </span>
                  ) : null}
                </NavLink>
              );
            })}
          </div>
        </div>
      </div>
    </Portal>
  );
}

/**
 * Meta's authorisation comes back through the address bar: `?conectado=` once the account
 * is linked. It is announced once and taken off the address so a reload does not repeat it.
 */
function useConnectionOutcome() {
  const [params, setParams] = useSearchParams();
  const connected = params.get("conectado");

  useEffect(() => {
    if (!connected) return;
    notify(connected === "1" ? "Cuenta conectada ✓" : `@${connected} conectada ✓`);
    setParams(
      (current) => {
        current.delete("conectado");
        return current;
      },
      { replace: true },
    );
  }, [connected, setParams]);
}
